import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Copy, X, Package } from "lucide-react";
import { ColorSwatchGrid } from "./ColorSwatchGrid";

export const newLine = (garments, supplied = false) => {
  const d = garments.find((g) => g.is_default) || garments[0];
  return supplied
    ? { customer_supplied: true, description: "", color: "", sizes: { ALL: 1 } }
    : { customer_supplied: false, garment_id: d?.id || null, color: d?.colors?.[0]?.name || "", sizes: {} };
};

function SizeGrid({ sizes, value, onChange, bigSet, idx }) {
  return (
    <div className="flex flex-wrap gap-1.5 mt-2">
      {sizes.map((sz) => (
        <div key={sz} className="w-14">
          <div className="text-[10px] font-mono text-center text-slate-500">{sz}{bigSet.has(sz.toUpperCase()) && <span className="text-amber-600"> +$</span>}</div>
          <Input data-testid={`line-${idx}-size-${sz}`} type="number" min="0" value={value[sz] ?? ""} placeholder="0"
            onChange={(e) => onChange({ ...value, [sz]: e.target.value === "" ? undefined : Math.max(0, +e.target.value) })} className="h-8 rounded-md text-center num px-1" />
        </div>
      ))}
    </div>
  );
}

function GarmentLine({ ln, idx, garments, bigSet, set }) {
  const g = garments.find((x) => x.id === ln.garment_id);
  const pcs = Object.values(ln.sizes || {}).reduce((a, b) => a + (+b || 0), 0);
  return (
    <>
      <div className="grid gap-2">
        <Select value={ln.garment_id || ""} onValueChange={(v) => { const ng = garments.find((x) => x.id === v); set({ ...ln, garment_id: v, color: ng?.colors?.[0]?.name || "", sizes: {} }); }}>
          <SelectTrigger data-testid={`line-${idx}-garment`} className="rounded-lg"><SelectValue placeholder="Garment" /></SelectTrigger>
          <SelectContent>{garments.map((x) => <SelectItem key={x.id} value={x.id}>{x.name}{x.style_number ? ` · ${x.style_number}` : ""}</SelectItem>)}</SelectContent>
        </Select>
        {(g?.colors || []).length > 0 ? (
          <div data-testid={`line-${idx}-color`}>
            <div className="text-[11px] text-slate-500 mb-1">Colour: <b className="text-slate-800" data-testid={`line-${idx}-color-name`}>{ln.color || "—"}</b></div>
            <div className="max-h-44 overflow-y-auto pr-1"><ColorSwatchGrid colors={g.colors} selected={ln.color} onSelect={(c) => set({ ...ln, color: c.name })} prefix={`line-${idx}-swatch`} size="sm" /></div>
          </div>
        ) : <Input data-testid={`line-${idx}-color`} placeholder="Colour" value={ln.color} onChange={(e) => set({ ...ln, color: e.target.value })} className="rounded-lg" />}
      </div>
      <SizeGrid sizes={g?.sizes?.length ? g.sizes : ["S", "M", "L", "XL", "2XL"]} value={ln.sizes || {}} onChange={(v) => set({ ...ln, sizes: v })} bigSet={bigSet} idx={idx} />
      <div className="text-[11px] text-slate-500 mt-1.5 num" data-testid={`line-${idx}-pcs`}>{pcs} pcs</div>
    </>
  );
}

function SuppliedLine({ ln, idx, set }) {
  return (
    <div className="grid grid-cols-3 gap-2">
      <Input data-testid={`line-${idx}-desc`} className="col-span-2 rounded-lg" placeholder="Customer garment (e.g. jackets)" value={ln.description} onChange={(e) => set({ ...ln, description: e.target.value })} />
      <Input data-testid={`line-${idx}-qty`} type="number" min="1" className="rounded-lg num" value={ln.sizes?.ALL ?? ""} onChange={(e) => set({ ...ln, sizes: { ALL: Math.max(0, +e.target.value) } })} />
    </div>
  );
}

export function OrderLines({ lines, setLines, garments, bigSizes }) {
  const bigSet = new Set((bigSizes || []).map((x) => String(x).toUpperCase()));
  const set = (i, v) => setLines(lines.map((l, idx) => (idx === i ? v : l)));
  const total = lines.reduce((a, l) => a + Object.values(l.sizes || {}).reduce((x, y) => x + (+y || 0), 0), 0);
  return (
    <div data-testid="order-lines">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-head font-bold">Garments</h3>
        <span className="text-xs font-mono text-slate-500" data-testid="order-total-pcs">{total} pcs total · discounts use the whole order</span>
      </div>
      <div className="space-y-3">
        {lines.map((ln, i) => (
          <div key={i} className={`relative border rounded-xl p-3 ${ln.customer_supplied ? "border-amber-200 bg-amber-50/40" : "border-slate-200"}`} data-testid={`line-${i}`}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{ln.customer_supplied ? "Customer-supplied" : `Line ${i + 1}`}</span>
              <div className="flex gap-1">
                <button data-testid={`line-${i}-dup`} title="Duplicate (e.g. another colour)" onClick={() => setLines([...lines.slice(0, i + 1), { ...ln, sizes: {} }, ...lines.slice(i + 1)])} className="p-1 text-slate-400 hover:text-[#2495D3]"><Copy size={14} /></button>
                <button data-testid={`line-${i}-remove`} onClick={() => setLines(lines.filter((_, idx) => idx !== i))} className="p-1 text-slate-400 hover:text-red-500"><X size={15} /></button>
              </div>
            </div>
            {ln.customer_supplied ? <SuppliedLine ln={ln} idx={i} set={(v) => set(i, v)} /> : <GarmentLine ln={ln} idx={i} garments={garments} bigSet={bigSet} set={(v) => set(i, v)} />}
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-2 mt-3">
        <Button data-testid="add-garment-line" variant="outline" size="sm" className="rounded-lg" onClick={() => setLines([...lines, newLine(garments)])}><Plus size={14} className="mr-1" />Garment / colour</Button>
        <Button data-testid="add-supplied-line" variant="outline" size="sm" className="rounded-lg" onClick={() => setLines([...lines, newLine(garments, true)])}><Package size={14} className="mr-1" />Customer-supplied</Button>
      </div>
    </div>
  );
}
